import React, { useEffect, useState } from "react";
import { Container } from "reactstrap";

//Import Components
// Redux
import { Box, Grid, Typography } from "@mui/material";
import { connect, useDispatch } from 'react-redux';
import Notification from "../../../components/Common/Notification";
import Table from "../../../components/Common/Table";
import CustomButton from "../../../components/Form/CustomButton";
import CustomSelect from "../../../components/Form/CustomSelect";
import CustomTextField from "../../../components/Form/CustomTextField";
import { calculateQuotation, getGeneral, resetQuotationActionState } from "../../../store/actions";
import LoadingDialog from "../../../components/Common/LoadingDialog";
import Helpers from "../../../helpers/helpers";

const QuotationLoanCalculator = ({ isEdit, calculateResult, calculationError, loanInterestRatesList, formObj, handleFormObjChange }) => {
    const dispatch = useDispatch();
    const [loading, setIsLoading] = useState(false);
    const [values, setFormValues] = useState(formObj || {});
    const [notification, setNotification] = useState({
        open: false,
        msg: "",
        type: "success",
    });
    const resultData = calculateResult?.data || (isEdit ? formObj?.pricing : null) || {};

    useEffect(() => {
        dispatch(resetQuotationActionState());
        dispatch(getGeneral({ type: "loan_interest_rates" }));
    }, [dispatch])

    useEffect(() => {
        if (calculateResult?.code === 'error') {
            setIsLoading(false);
            setNotification({
                open: true,
                msg: calculateResult?.message || "Failed to calculate loan. Please try again.",
                type: "error",
            });
        } else if (calculateResult?.code === 'success') {
            setIsLoading(false);
        }
    }, [calculateResult])

    useEffect(() => {
        if (!loading || !calculationError || (Array.isArray(calculationError) && calculationError.length === 0)) return;
        setIsLoading(false);
        setNotification({
            open: true,
            msg: calculationError?.message || calculationError?.[0] || "Failed to calculate loan. Please try again.",
            type: "error",
        });
    }, [calculationError, loading]);

    const header = [
        {
            Header: "Year",
            accessor: (cellProps) => {
                return (cellProps?.year || "-")
            },
        },
        {
            Header: "Interest ($)",
            accessor: (cellProps) => {
                return (cellProps?.interest || "-")
            },
        },
        {
            Header: "Monthly Instalment ($)",
            accessor: (cellProps) => {
                return (<div style={{ display: "flex", justifyContent: "flex-end" }}>{cellProps?.monthly || "-"}</div>)
            },
        },
        {
            Header: "Last Instalment ($)",
            accessor: (cellProps) => {
                return (<div style={{ display: "flex", justifyContent: "flex-end" }}>{cellProps?.last || "-"}</div>)
            },
        },
    ]

    const handleCalculate = () => {
        setIsLoading(true);
        dispatch(resetQuotationActionState());
        let payload = {
            machine_price: Number(values?.price || 0),
            overtrade: Number(values?.overtrade || 0),
            coe: Number(values?.coe || 0),
            insurance: Number(values?.insurance || 0),
            admin_fee: Number(values?.fee || 0),
            other_charges: Number(values?.other || 0),
            downpayment: Number(values?.downpayment || 0),
            loan_term: Number(values?.term || 0),
            interest_rate: Number(values?.rate || 0),
        }
        dispatch(calculateQuotation(payload));
    }

    const calculateTotalPrice = () => {
        let total = 0;
        total += parseFloat(values?.price || 0);
        total += parseFloat(values?.overtrade || 0);
        total += parseFloat(values?.coe || 0);
        total += parseFloat(values?.insurance || 0);
        total += parseFloat(values?.fee || 0);
        total += parseFloat(values?.other || 0);
        return total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    const handleFormChange = (key, value) => {
        setFormValues((prev) => ({
            ...prev,
            [key]: value,
        }));
        handleFormObjChange(key, value);
    }

    const handleInterestRateChange = (value) => {
        if (!isEdit) {
            handleFormChange('rate', value);
            return;
        }

        const selectedRate = loanInterestRatesList?.find(
            (option) => String(option?.id) === String(value)
        );
        const rate = selectedRate?.interest_rate ?? "";

        setFormValues((prev) => ({
            ...prev,
            interest_rate_id: value,
            rate,
        }));
        handleFormObjChange('interest_rate_id', value);
        handleFormObjChange('rate', rate);
    };

    const fieldError = (field) => {
        if (calculationError?.code !== "invalid_field") return null;
        const error = calculationError?.errors?.[field];
        return Array.isArray(error) ? error[0] : error;
    };

    const editSummary = (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
                gap: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#F5F5F5",
                textAlign: "center",
            }}
        >
            <Box>
                <Typography fontSize={20} fontWeight={600} color="#ED1A3D">
                    {resultData?.monthly_instalment || "-"}
                </Typography>
                <Typography fontSize={12} color="#808588">Est. Monthly Instalment</Typography>
            </Box>
            <Box>
                <Typography fontSize={20} fontWeight={600}>
                    {resultData?.loan_amount || "-"}
                </Typography>
                <Typography fontSize={12} color="#808588">Loan Amount</Typography>
            </Box>
            <Box>
                <Typography fontSize={20} fontWeight={600}>{values?.term || "-"} mths</Typography>
                <Typography fontSize={12} color="#808588">{(Number(values?.rate)?.toFixed(2)) || "-"}% P.A.</Typography>
            </Box>
            <Box>
                <Typography fontSize={20} fontWeight={600}>
                    {resultData?.grand_total || "-"}
                </Typography>
                <Typography fontSize={12} color="#808588">Grand Total</Typography>
            </Box>
        </Box>
    );
    
    return (
        <React.Fragment>
            <div className="">
                <LoadingDialog open={loading} />
                <Notification notification={notification} setNotification={setNotification} />
                <Container fluid>
                    <Grid container spacing={2}>
                        <Grid item lg={6} md={isEdit? 12: 6} sm={12} xs={12} pr={isEdit ? 0 : 2}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} style={{ fontWeight: 600, }}>
                                    Vehicle Pricing
                                </Grid>

                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Machine Price"}
                                        title={"Machine Price"}
                                        type={"number"}
                                        is2dp={true}
                                        values={values?.price || ""}
                                        onChange={(e) => handleFormChange('price', e)}
                                        error={fieldError('machine_price')}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Overtrade"}
                                        title={"Overtrade"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.overtrade || ""}
                                        onChange={(e) => handleFormChange('overtrade', e)}
                                        error={fieldError('overtrade')}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"COE"}
                                        title={"COE"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.coe || ""}
                                        onChange={(e) => handleFormChange('coe', e)}
                                        error={fieldError('coe')}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Insurance"}
                                        title={"Insurance"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.insurance || ""}
                                        onChange={(e) => handleFormChange('insurance', e)}
                                        error={fieldError('insurance')}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Admin Fee"}
                                        title={"Admin Fee"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.fee || ""}
                                        onChange={(e) => handleFormChange('fee', e)}
                                        error={fieldError('admin_fee')}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <CustomTextField
                                        placeholder={"Other Charges"}
                                        title={"Other Charges"}
                                        type={"number"}
                                        is2dp={true}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                        values={values?.other || ""}
                                        onChange={(e) => handleFormChange('other', e)}
                                        error={fieldError('other_charges')}
                                    />
                                </Grid>
                                <Grid item xs={12} display={"flex"} justifyContent={"flex-end"} pt={5}>
                                    <span className="text-muted">Total price: </span> <span className="text-bold mx-1">  ${calculateTotalPrice()}</span>
                                </Grid>

                                <Grid item xs={12} style={{ fontWeight: 600, paddingTop: 20 }}>
                                    Loan Details
                                </Grid>

                                <Grid item xs={12}>
                                    <CustomTextField
                                        placeholder={"Downpayment"}
                                        title={"Downpayment"}
                                        type={"number"}
                                        is2dp={true}
                                        values={values?.downpayment || ""}
                                        onChange={(e) => handleFormChange('downpayment', e)}
                                        error={fieldError('downpayment')}
                                        InputProps={{
                                            startAdornment: <div style={{ paddingLeft: 6, paddingRight: 10 }}>$ </div>,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomSelect
                                        placeholder={"Loan Terms (mth)"}
                                        title={"Loan Terms (mth)"}
                                        values={values?.term || ""}
                                        data={Helpers.loanTermOptions}
                                        onChange={(e) => handleFormChange('term', e)}
                                        error={fieldError('loan_term')}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <CustomSelect
                                        placeholder={"Interest Rate p.a."}
                                        title={"Interest Rate p.a."}
                                        data={loanInterestRatesList || []}
                                        labelName={'interest_rate'}
                                        valueName={isEdit ? 'id' : 'interest_rate'}
                                        values={isEdit ? values?.interest_rate_id : values?.rate || ""}
                                        onChange={handleInterestRateChange}
                                        error={fieldError('interest_rate')}
                                    />
                                </Grid>
                                <Grid item xs={12} mt={2}>
                                    <CustomButton
                                        title={"Calculate"}
                                        press={(e) => handleCalculate()}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item lg={6} md={isEdit? 12: 6} sm={12} xs={12} >
                            {!isEdit ? <>
                                <span style={{ fontWeight: 600, paddingTop: 20 }}>
                                    Instalment Reference
                                </span>
                                <Box style={{ display: "flex", flexDirection: "column", paddingTop: "30px", padding: "10px", paddingBottom: "15px" }} >
                                    <span style={{ fontWeight: 600, color: "#D13536", fontSize: 32 }}>{resultData?.monthly_instalment || "-"}</span>
                                    <span className="text-muted">Est. Monthly Instalment</span>
                                    <Grid container spacing={2} py={2}>
                                        <Grid item lg={4} md={4} sm={4} xs={4}>
                                            <div style={{ fontWeight: 600, fontSize: 24 }}>{resultData?.loan_amount || "-"}</div>
                                            <div className="text-muted">Loan Amount</div>
                                        </Grid>
                                        <Grid item lg={4} md={4} sm={4} xs={4}>
                                            <div style={{ fontWeight: 600, fontSize: 24 }}>{resultData?.interest_amount || "-"}</div>
                                            <div className="text-muted">Interest</div>
                                        </Grid>
                                        <Grid item lg={4} md={4} sm={4} xs={4}>
                                            <div style={{ fontWeight: 600, fontSize: 24 }}>{resultData?.grand_total || "-"}</div>
                                            <div className="text-muted">Grand Total</div>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </> :
                                <div className="mb-3">
                                    {editSummary}
                                </div>

                            }

                            <Table
                                header={header}
                                data={(resultData?.instalment_schedule || []).map(item => ({
                                    ...item,
                                    monthly: item.monthly_instalment,
                                    last: item.last_instalment,
                                }))}
                                pageSize={resultData?.instalment_schedule?.length || 10}
                                totalPage={resultData?.last_page}
                                headerBackground={"#F5F5F5"}
                                headerTitleColor={"#808588"}
                            />
                            <span className="text-muted pt-5">Note: The results are indicative and may vary based on bank approval.</span>
                        </Grid>

                    </Grid>
                </Container>
            </div >
            {/* <Footer></Footer> */}
        </React.Fragment >
    );

}

function mapStateToProps({ Login, Quotation, Layout, General }) {
    return {
        userPermission: Login.permissionArry,
        leftSideBarType: Layout.leftSideBarType,
        calculateResult: Quotation.quotationCalculation,
        calculationError: Quotation.error,
        loanInterestRatesList: General.loan_interest_rates,
    };
}

export default connect(mapStateToProps)(QuotationLoanCalculator);
